import { MinusIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/business/market-shop/button'
import { Icon } from '@/components/ui/icon'
import { Typography } from '@/components/ui/typography'
import { popupFactory } from '@/factories'
import { MarketShopProductModel } from '@/models'
import { ProductDetails } from '@/pages/market-shop/product-details'
import { formatPrice } from '@/utils'

import * as S from './cart-product-item.styled'

export type CartProductItemProps = {
  product: MarketShopProductModel
  tax: number
  quantity: number
  note?: string
  disabled?: boolean
  onAddToCart: (product: MarketShopProductModel) => void
  onSubtractFromCart: (product: MarketShopProductModel) => void
  onRemoveFromCart: (product: MarketShopProductModel) => void
}

export const CartProductItem = ({
  product,
  tax,
  quantity,
  note,
  disabled,
  onAddToCart,
  onSubtractFromCart,
  onRemoveFromCart,
}: CartProductItemProps) => {
  const { t } = useTranslation(['market-shop'])
  const canAddToCart = product.unlimited ? true : quantity < product.quantity

  const handleProductTitleClick = () => {
    popupFactory.open(ProductDetails, { product }, { suspendCurrent: true })
  }

  const handleAddToCart = () => onAddToCart(product)

  const handleSubtractFromCart = () => onSubtractFromCart(product)

  const handleRemoveFromCart = () => onRemoveFromCart(product)

  return (
    <S.CartProductItemContainer>
      <S.CartProductTopRow>
        <S.ProductTitle
          forwardedAs="button"
          type="button"
          color="black800"
          size="text-medium"
          weight="medium"
          breakLines={3}
          onClick={handleProductTitleClick}
        >
          {product.summary}
        </S.ProductTitle>
        <S.Controls>
          <S.Prices>
            <Typography
              as="span"
              color="black800"
              size="text-small"
              weight="semibold"
              whiteSpace="nowrap"
            >
              {formatPrice(product.price * quantity, { zeroPrice: true })}
            </Typography>
            <Typography
              as="span"
              color="black500"
              size="text-small"
              weight="medium"
              whiteSpace="nowrap"
            >
              {t('market-shop:cartProductItem.taxSuffix', {
                tax: formatPrice(tax, { zeroPrice: true }),
              })}
            </Typography>
          </S.Prices>
          <Button
            onlyBox
            use="transparent-dangerous"
            size="small"
            disabled={disabled}
            onClick={handleRemoveFromCart}
          >
            <Icon icon={TrashIcon} />
          </Button>
          <Button
            onlyBox
            use="secondary-gray"
            size="small"
            disabled={disabled}
            onClick={handleSubtractFromCart}
          >
            <Icon icon={MinusIcon} />
          </Button>
          <S.Count>
            <Typography as="span" color="black800" size="text-small" weight="semibold">
              {quantity}
            </Typography>
          </S.Count>
          <Button
            onlyBox
            use="primary"
            size="small"
            disabled={!canAddToCart || disabled}
            onClick={handleAddToCart}
          >
            <Icon icon={PlusIcon} />
          </Button>
        </S.Controls>
      </S.CartProductTopRow>
      {note && (
        <S.ProductDescription
          forwardedAs="span"
          color="black500"
          size="text-small"
          wordBreak="break-word"
        >
          {note}
        </S.ProductDescription>
      )}
    </S.CartProductItemContainer>
  )
}
